import { useState, useEffect, useMemo, useCallback } from 'react';
import { X, Loader2, Mail, Send, AlertTriangle, Check, ShieldAlert, Paperclip } from 'lucide-react';
import { arDunningApi } from '../../services/arDunningApi';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../ui/Toast';
import { formatMoney, formatAmountPlain, formatDate, templateLanguage, senderNameFromEmail } from '../../utils/arDunningFormat';
import type { ArOpenItem, ArTemplate, ArCompanyOption } from '../../types/arDunning';

interface Props {
    items: ArOpenItem[];
    /** How many issued-RiBa items the caller removed from the selection (shown as a hint). */
    excludedRibaCount?: number;
    /** Initial dunning level (drives audience routing). */
    initialLevel?: number;
    /** 'combined' = ONE internal e-mail covering all selected items, grouped by customer. */
    mode?: 'per-customer' | 'combined';
    companies?: ArCompanyOption[];
    canEdit: boolean;
    onClose: () => void;
    /** Fired after at least one mail was sent, so the list/stats can refresh. */
    onSent: () => void;
}

type GroupStatus = 'idle' | 'sending' | 'sent' | 'failed';

interface GroupDraft {
    customerId: string;
    /** The real etl.Customer.CustomerID (empty if the item had none) — for contact persistence. */
    realCustomerId: string;
    companyCode: string;
    customerName: string;
    currency: string;
    lang: 'DE' | 'FR' | 'IT' | 'EN';
    sellerEmail: string;
    sellerName: string;
    origCustomerEmail: string;
    items: ArOpenItem[];
    totalFCY: number;
    totalEUR: number;
    maxDaysOverdue: number;
    // editable
    to: string;
    cc: string;
    subject: string;
    body: string;
    /** Greeting name currently substituted into the body (re-derived when To changes). */
    recipientName: string;
    included: boolean;
    status: GroupStatus;
    error: string | null;
    templateMissing: boolean;
}

const LEVEL_ROUTING: Record<number, { audience: 'internal' | 'customer'; label: string }> = {
    1: { audience: 'internal', label: 'Internal — sent to the responsible sales manager.' },
    2: { audience: 'customer', label: 'Customer — sent to the customer; sales manager in CC.' },
    3: { audience: 'customer', label: 'Customer — sent to the customer; CC sales manager + central credit control.' },
};

function escapeHtml(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
function stripHtml(s: string): string {
    return s.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}
function parseEmails(csv: string): { email: string }[] {
    return csv.split(/[,;]+/).map(e => e.trim()).filter(Boolean).map(email => ({ email }));
}
function isValidEmail(e: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}
function substitute(text: string, vars: Record<string, string>): string {
    return text.replace(/\{(\w+)\}/g, (m, key) => (key in vars ? vars[key] : m));
}

/**
 * Compose & preview dunning reminders for a set of open items. Items are grouped by
 * customer (templates are customer-wide, one mail per customer), routed by level
 * (L1 internal→seller · L2/L3 customer direct + CC), substituted client-side, and
 * sent as the logged-in user via the emails/send endpoint. Customer-facing sends
 * require an explicit confirmation. After each send, a ReminderSent action is recorded
 * for every item in the group (the endpoint records the first; the rest are looped).
 */
export function SendReminderModal({ items, excludedRibaCount = 0, initialLevel, mode = 'per-customer', companies, canEdit, onClose, onSent }: Props) {
    const { user } = useAuth();
    const { toast } = useToast();
    const combined = mode === 'combined';
    const senderName = senderNameFromEmail(user?.email);
    const senderEmail = (user?.email || '').toLowerCase();

    const [level, setLevel] = useState<number>(() => {
        if (combined) return 1; // combined mode = internal info mail, recorded as L1
        const l = initialLevel && initialLevel >= 1 && initialLevel <= 3 ? initialLevel : 0;
        if (l) return l;
        // Default to the most urgent suggested next level across the selection, else L1.
        const maxNext = Math.max(0, ...items.map(i => i.NextLevel || i.SuggestedLevel || 0));
        return Math.min(3, Math.max(1, maxNext || 1));
    });
    const audience = combined ? 'internal' : LEVEL_ROUTING[level].audience;

    const [templates, setTemplates] = useState<ArTemplate[] | null>(null);
    const [centralEmails, setCentralEmails] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [drafts, setDrafts] = useState<GroupDraft[]>([]);
    const [confirmed, setConfirmed] = useState(false);
    const [attachStatement, setAttachStatement] = useState(false);
    const [busy, setBusy] = useState(false);
    const [regenNonce, setRegenNonce] = useState(0);

    const companyName = useCallback((code: string) => {
        const c = companies?.find(x => x.CompanyCode === code);
        return c?.CompanyName ?? code.toUpperCase();
    }, [companies]);

    // Load templates + central controllers once.
    useEffect(() => {
        let cancelled = false;
        void (async () => {
            try {
                const [tpl, cfg] = await Promise.all([arDunningApi.listTemplates(), arDunningApi.getConfig()]);
                if (cancelled) return;
                setTemplates(tpl.templates);
                setCentralEmails(cfg.recipients.filter(r => r.IsCentral).map(r => r.UserEmail));
            } catch (e) {
                if (!cancelled) toast(e instanceof Error ? e.message : 'Failed to load templates', 'error');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [toast]);

    const appUrl = useMemo(() => `${window.location.origin}/accounts-receivable`, []);

    // Build one draft per customer whenever the level (routing/template) changes.
    // In combined mode a single internal draft covers ALL items, grouped by customer.
    useEffect(() => {
        if (!templates && !combined) return;
        const audienceForLevel = combined ? 'internal' : LEVEL_ROUTING[level].audience;
        const wantAudience = audienceForLevel === 'internal' ? 'Internal' : 'Customer';

        const byCustomer = new Map<string, ArOpenItem[]>();
        for (const it of items) {
            const key = it.CustomerID || `${it.CompanyCode}-${it.CustomerCode ?? it.InvoiceNo}`;
            const arr = byCustomer.get(key) ?? [];
            arr.push(it);
            byCustomer.set(key, arr);
        }

        if (combined) {
            // ONE internal e-mail: greeting from the recipient, one section per customer.
            const sellerEmails = [...new Set(items.map(i => (i.SalesManagerEmail || '').trim().toLowerCase()).filter(Boolean))];
            const firstSeller = items.find(i => (i.SalesManagerEmail || '').trim());
            const recipientName = sellerEmails.length === 1
                ? (firstSeller?.SalesManagerName || senderNameFromEmail(sellerEmails[0]))
                : 'all';
            const totalEUR = items.reduce((s, i) => s + (i.OpenAmountEUR || 0), 0);

            const sections: string[] = [];
            for (const [customerId, groupItems] of byCustomer) {
                const first = groupItems[0];
                const name = first.CustomerName ?? first.CustomerCode ?? customerId;
                const groupEUR = groupItems.reduce((s, i) => s + (i.OpenAmountEUR || 0), 0);
                const list = `<ul>${groupItems
                    .map(i => `<li>${escapeHtml(i.InvoiceNo)} — ${escapeHtml(formatMoney(i.OpenAmountFCY, i.Currency))}${i.DueDate ? ` (due ${escapeHtml(formatDate(i.DueDate))}${i.DaysOverdue > 0 ? `, ${i.DaysOverdue}d overdue` : ''})` : ''}</li>`)
                    .join('')}</ul>`;
                sections.push(`<p><strong>${escapeHtml(name)}</strong> (${escapeHtml(first.CompanyCode.toUpperCase())}) — ${groupItems.length} invoice${groupItems.length === 1 ? '' : 's'}, ${escapeHtml(formatMoney(groupEUR))}:</p>${list}`);
            }
            const body = `<p>Hi ${escapeHtml(recipientName)},</p>`
                + `<p>The following customer${byCustomer.size === 1 ? ' has' : 's have'} overdue invoice(s):</p>`
                + sections.join('')
                + `<p>Total: ${escapeHtml(formatMoney(totalEUR))} across ${items.length} invoice${items.length === 1 ? '' : 's'}. Please follow up. Open in the app: ${appUrl}</p>`;

            const first = items[0];
            setDrafts([{
                customerId: '__combined__', realCustomerId: '', companyCode: first.CompanyCode,
                customerName: `${byCustomer.size} customer${byCustomer.size === 1 ? '' : 's'}`,
                currency: 'EUR', lang: 'EN',
                sellerEmail: sellerEmails[0] ?? '', sellerName: recipientName, origCustomerEmail: '',
                items: [...items], totalFCY: 0, totalEUR,
                maxDaysOverdue: Math.max(0, ...items.map(i => i.DaysOverdue || 0)),
                to: sellerEmails.join(', '), cc: '',
                subject: `AR follow-up: ${items.length} overdue invoice${items.length === 1 ? '' : 's'} · ${byCustomer.size} customer${byCustomer.size === 1 ? '' : 's'}`,
                body, recipientName,
                included: true, status: 'idle', error: null, templateMissing: false,
            }]);
            setConfirmed(false);
            return;
        }
        if (!templates) return;

        const pickTemplate = (lang: string): ArTemplate | null => {
            const atLevel = templates.filter(t => t.DunningLevel === level && t.Audience === wantAudience);
            return atLevel.find(t => t.LanguageCode === lang)
                ?? atLevel.find(t => t.LanguageCode === 'EN')
                ?? atLevel[0]
                ?? null;
        };

        const next: GroupDraft[] = [];
        for (const [customerId, groupItems] of byCustomer) {
            const first = groupItems[0];
            const currencies = new Set(groupItems.map(i => i.Currency));
            const singleCcy = currencies.size === 1;
            const currency = singleCcy ? first.Currency : 'EUR';
            const totalFCY = groupItems.reduce((s, i) => s + (i.OpenAmountFCY || 0), 0);
            const totalEUR = groupItems.reduce((s, i) => s + (i.OpenAmountEUR || 0), 0);
            const groupTotal = singleCcy ? totalFCY : totalEUR;
            const maxDaysOverdue = Math.max(0, ...groupItems.map(i => i.DaysOverdue || 0));
            const lang = templateLanguage(first.LanguageCode);
            const sellerEmail = (first.SalesManagerEmail || '').trim();
            const sellerName = first.SalesManagerName || '';
            const origCustomerEmail = (first.CustomerEmail || '').trim();
            // Greeting target: internal mails address the seller (the To recipient);
            // customer mails address the customer.
            const recipientName = audienceForLevel === 'internal'
                ? (sellerName || (sellerEmail ? senderNameFromEmail(sellerEmail) : ''))
                : (first.CustomerName ?? first.CustomerCode ?? '');

            const invoiceListHtml = `<ul>${groupItems
                .map(i => `<li>${escapeHtml(i.InvoiceNo)} — ${escapeHtml(formatMoney(i.OpenAmountFCY, i.Currency))}${i.DueDate ? ` (due ${escapeHtml(formatDate(i.DueDate))})` : ''}</li>`)
                .join('')}</ul>`;
            const invoiceNos = groupItems.map(i => i.InvoiceNo).join(', ');

            const vars: Record<string, string> = {
                CustomerName: first.CustomerName ?? first.CustomerCode ?? customerId,
                CompanyName: companyName(first.CompanyCode),
                InvoiceNo: invoiceNos,
                InvoiceList: invoiceListHtml,
                OpenAmount: formatAmountPlain(groupTotal),
                TotalOpenAmount: formatAmountPlain(groupTotal),
                Currency: currency,
                DaysOverdue: String(maxDaysOverdue),
                SenderName: senderName,
                SellerName: sellerName,
                RecipientName: recipientName,
                AppUrl: appUrl,
            };

            const tpl = pickTemplate(lang);
            const subject = tpl ? stripHtml(substitute(tpl.Subject, vars)) : '';
            const body = tpl ? substitute(tpl.BodyHtml, vars) : '';

            // Recipient routing by level. "Send reminder" never auto-fills the To — the
            // address (seller for L1, customer for L2/L3) is offered as a one-click
            // suggestion below the field and must be chosen consciously. The combined
            // "Internal e-mail" mode keeps its seller prefill.
            const to = '';
            let cc = '';
            if (audienceForLevel !== 'internal') {
                const ccList = [sellerEmail, ...(level === 3 ? centralEmails : [])]
                    .map(e => e.trim())
                    .filter(e => e && e.toLowerCase() !== senderEmail);
                cc = Array.from(new Set(ccList)).join(', ');
            }

            next.push({
                customerId, realCustomerId: first.CustomerID ?? '', companyCode: first.CompanyCode, customerName: vars.CustomerName, currency, lang,
                sellerEmail, sellerName, origCustomerEmail, items: groupItems, totalFCY, totalEUR, maxDaysOverdue,
                to, cc, subject, body, recipientName, included: true, status: 'idle', error: null, templateMissing: !tpl,
            });
        }
        next.sort((a, b) => Math.abs(b.totalEUR) - Math.abs(a.totalEUR));
        setDrafts(next);
        setConfirmed(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templates, level, regenNonce, centralEmails]);

    // Esc closes (unless sending).
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose, busy]);

    const patchDraft = (customerId: string, patch: Partial<GroupDraft>) =>
        setDrafts(prev => prev.map(d => (d.customerId === customerId ? { ...d, ...patch } : d)));

    /** Patch the To field; for internal mails also swap the body greeting to the new recipient. */
    const changeTo = (d: GroupDraft, value: string) => {
        const patch: Partial<GroupDraft> = { to: value };
        if (audience === 'internal') {
            const firstEmail = parseEmails(value)[0]?.email ?? '';
            const nextName = firstEmail
                ? (d.sellerName && firstEmail.toLowerCase() === d.sellerEmail.toLowerCase() ? d.sellerName : senderNameFromEmail(firstEmail))
                : '';
            if (nextName && d.recipientName && nextName !== d.recipientName) {
                patch.body = d.body.replace(d.recipientName, nextName); // greeting is the first occurrence
                patch.recipientName = nextName;
            }
        }
        patchDraft(d.customerId, patch);
    };

    const includedDrafts = drafts.filter(d => d.included);
    const missingRecipient = includedDrafts.filter(d => parseEmails(d.to).length === 0);
    const invalidRecipient = includedDrafts.filter(d =>
        [...parseEmails(d.to), ...parseEmails(d.cc)].some(r => !isValidEmail(r.email)));
    const canSend = canEdit && !busy && !loading && includedDrafts.length > 0
        && missingRecipient.length === 0 && invalidRecipient.length === 0
        && (audience === 'internal' || confirmed);

    const send = async () => {
        if (!canSend) return;
        setBusy(true);
        let okCount = 0;
        let failCount = 0;
        try {
            for (const d of drafts.filter(x => x.included)) {
                patchDraft(d.customerId, { status: 'sending', error: null });
                const to = parseEmails(d.to);
                const cc = parseEmails(d.cc);
                try {
                    // Persist a manually entered / changed customer address so it's remembered.
                    if (audience === 'customer' && to[0] && d.realCustomerId && to[0].email.toLowerCase() !== d.origCustomerEmail.toLowerCase()) {
                        try {
                            await arDunningApi.upsertCustomerContact({ companyCode: d.companyCode, customerId: d.realCustomerId, email: to[0].email });
                        } catch { /* best-effort: don't block the send */ }
                    }

                    const res = await arDunningApi.sendEmail({
                        audience,
                        to, cc,
                        subject: d.subject,
                        htmlBody: d.body,
                        plainBodyPreview: stripHtml(d.body).slice(0, 4000),
                        confirmed: audience === 'customer',
                        entityType: 'Invoice',
                        entityRef: d.items[0].ItemKey,
                        dunningLevel: level,
                        contextPayload: { customerId: d.customerId, itemKeys: d.items.map(i => i.ItemKey), count: d.items.length, totalEUR: d.totalEUR },
                        // Attach the customer's account statement PDF when requested (customer path only;
                        // built server-side from the scope-enforced query, never client bytes).
                        ...(audience === 'customer' && attachStatement && d.realCustomerId
                            ? { attachStatement: { customerId: d.realCustomerId, companyCode: d.companyCode, lang: d.lang } }
                            : {}),
                    });

                    // Endpoint recorded ReminderSent for items[0]; record the rest, linked to the same EmailLog.
                    for (const it of d.items.slice(1)) {
                        try {
                            await arDunningApi.createAction({
                                entityType: 'Invoice', entityRef: it.ItemKey, actionType: 'ReminderSent',
                                dunningLevel: level, emailLogId: res.emailLogId, notes: `L${level} ${audience} reminder (batch)`,
                            });
                        } catch { /* one item's action failing shouldn't fail the whole send */ }
                    }
                    patchDraft(d.customerId, { status: 'sent' });
                    okCount++;
                } catch (e) {
                    patchDraft(d.customerId, { status: 'failed', error: e instanceof Error ? e.message : 'Send failed' });
                    failCount++;
                }
            }
            toast(`Reminders sent: ${okCount}${failCount ? `, ${failCount} failed` : ''}`, failCount ? 'info' : 'success');
            if (okCount > 0) onSent();
            if (failCount === 0) onClose();
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={() => { if (!busy) onClose(); }}>
            <div className="flex max-h-[92vh] w-full max-w-3xl flex-col rounded-lg bg-white shadow-xl" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="flex items-start justify-between border-b border-slate-200 p-4">
                    <div>
                        <h3 className="flex items-center gap-2 text-lg font-semibold text-slate-800"><Mail className="h-5 w-5 text-indigo-600" /> {combined ? 'Send internal e-mail' : 'Send dunning reminders'}</h3>
                        <p className="mt-0.5 text-sm text-slate-500">
                            {combined
                                ? `${items.length} invoice${items.length === 1 ? '' : 's'} · one combined e-mail · sent as ${user?.email}`
                                : `${drafts.length} customer${drafts.length === 1 ? '' : 's'} · ${items.length} invoice${items.length === 1 ? '' : 's'} · sent as ${user?.email}`}
                        </p>
                    </div>
                    <button onClick={() => { if (!busy) onClose(); }} className="rounded p-1 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
                </div>

                {/* Level / routing */}
                <div className="border-b border-slate-100 px-4 py-3">
                    {combined ? (
                        <p className="text-xs text-slate-500">
                            <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">internal</span>
                            One e-mail with all selected items, grouped by customer — e.g. to inform the responsible sales manager. Recorded as L1 internal reminder per item.
                        </p>
                    ) : (
                        <>
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Level</span>
                                {[1, 2, 3].map(l => (
                                    <button key={l} disabled={busy} onClick={() => { setLevel(l); setRegenNonce(n => n + 1); }}
                                        className={`rounded-md border px-3 py-1 text-sm font-medium ${level === l ? 'border-indigo-400 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'} disabled:opacity-50`}>L{l}</button>
                                ))}
                                <span className={`ml-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${audience === 'customer' ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600'}`}>{audience}</span>
                            </div>
                            <p className="mt-1.5 text-xs text-slate-500">{LEVEL_ROUTING[level].label}</p>
                        </>
                    )}
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-4">
                    {excludedRibaCount > 0 && (
                        <div className="mb-3 flex items-start gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            <span>{excludedRibaCount} issued-RiBa item{excludedRibaCount === 1 ? '' : 's'} excluded — collected by the bank via RiBa (SBF), not dunned.</span>
                        </div>
                    )}
                    {loading ? (
                        <div className="py-10 text-center text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
                    ) : drafts.length === 0 ? (
                        <p className="text-sm text-slate-500">No items to send.</p>
                    ) : (
                        <div className="space-y-3">
                            {drafts.map(d => (
                                <div key={d.customerId} className={`rounded-lg border p-3 ${d.status === 'sent' ? 'border-emerald-200 bg-emerald-50/40' : d.status === 'failed' ? 'border-rose-200 bg-rose-50/40' : 'border-slate-200'}`}>
                                    <div className="mb-2 flex items-center justify-between gap-2">
                                        <label className="flex min-w-0 items-center gap-2">
                                            <input type="checkbox" checked={d.included} disabled={busy} onChange={e => patchDraft(d.customerId, { included: e.target.checked })} />
                                            <span className="truncate text-sm font-semibold text-slate-800" title={d.customerName}>{d.customerName}</span>
                                            <span className="shrink-0 text-xs uppercase text-slate-400">{combined ? 'combined' : `${d.companyCode} · ${d.lang}`}</span>
                                        </label>
                                        <span className="shrink-0 text-xs text-slate-500">{d.items.length} inv · {formatMoney(d.totalEUR)}</span>
                                    </div>

                                    {d.templateMissing && (
                                        <div className="mb-2 flex items-center gap-1.5 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-800"><AlertTriangle className="h-3.5 w-3.5" /> No template for L{level}/{audience}. Add one in Settings.</div>
                                    )}
                                    {d.status === 'sent' && <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-emerald-700"><Check className="h-3.5 w-3.5" /> Sent</div>}
                                    {d.status === 'failed' && <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-rose-700"><AlertTriangle className="h-3.5 w-3.5" /> {d.error}</div>}

                                    <div className="grid gap-2 sm:grid-cols-2">
                                        <label className="block text-xs font-medium text-slate-500">To
                                            <input value={d.to} disabled={busy} onChange={e => changeTo(d, e.target.value)}
                                                placeholder={audience === 'customer' ? 'customer@example.com' : 'seller@dolder.com'}
                                                className={`mt-1 w-full rounded border px-2 py-1.5 text-sm ${parseEmails(d.to).length === 0 ? 'border-rose-300 bg-rose-50' : 'border-slate-300'}`} />
                                            {(() => {
                                                const suggestion = audience === 'customer' ? d.origCustomerEmail : d.sellerEmail;
                                                return suggestion && !d.to ? (
                                                    <button type="button" disabled={busy} onClick={() => changeTo(d, suggestion)} className="mt-1 text-xs font-normal text-indigo-600 hover:underline">
                                                        Use {suggestion}
                                                    </button>
                                                ) : null;
                                            })()}
                                        </label>
                                        <label className="block text-xs font-medium text-slate-500">Cc
                                            <input value={d.cc} disabled={busy} onChange={e => patchDraft(d.customerId, { cc: e.target.value })} className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm" />
                                        </label>
                                    </div>
                                    <label className="mt-2 block text-xs font-medium text-slate-500">Subject
                                        <input value={d.subject} disabled={busy} onChange={e => patchDraft(d.customerId, { subject: e.target.value })} className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm" />
                                    </label>
                                    <label className="mt-2 block text-xs font-medium text-slate-500">Body (HTML)
                                        <textarea value={d.body} disabled={busy} onChange={e => patchDraft(d.customerId, { body: e.target.value })} rows={5} className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 font-mono text-xs" />
                                    </label>
                                    <details className="mt-1.5">
                                        <summary className="cursor-pointer text-xs text-slate-400 hover:text-slate-600">Preview</summary>
                                        <div className="prose prose-sm mt-1 max-w-none rounded border border-slate-100 bg-slate-50 p-2 text-sm" dangerouslySetInnerHTML={{ __html: d.body }} />
                                    </details>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t border-slate-200 p-4">
                    {audience === 'customer' && (
                        <>
                            <label className="mb-2 flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                                <input type="checkbox" checked={attachStatement} disabled={busy} onChange={e => setAttachStatement(e.target.checked)} />
                                <span className="flex items-center gap-1"><Paperclip className="h-4 w-4 shrink-0 text-slate-500" /> Attach each customer's account statement (PDF) in their language.</span>
                            </label>
                            <label className="mb-3 flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                                <input type="checkbox" checked={confirmed} disabled={busy} onChange={e => setConfirmed(e.target.checked)} className="mt-0.5" />
                                <span className="flex items-center gap-1"><ShieldAlert className="h-4 w-4 shrink-0" /> I confirm these e-mails will be sent directly to the customers listed above.</span>
                            </label>
                        </>
                    )}
                    {missingRecipient.length > 0 && (
                        <p className="mb-2 text-xs text-rose-600">{missingRecipient.length} included customer{missingRecipient.length === 1 ? '' : 's'} still need a recipient address.</p>
                    )}
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-500">{combined ? 'one combined internal e-mail' : `${includedDrafts.length} of ${drafts.length} selected · one e-mail per customer`}</span>
                        <div className="flex gap-2">
                            <button onClick={() => { if (!busy) onClose(); }} className="rounded-md border px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50">Cancel</button>
                            <button onClick={send} disabled={!canSend} className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
                                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {combined ? 'Send internal e-mail' : `Send ${includedDrafts.length > 1 ? `${includedDrafts.length} reminders` : 'reminder'}`}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
